import React, { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Container, Form, Pagination, Row, Table,
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  assignShiftAllotment,
  getAllShiftAllotments,
  getAllShifts,
  getAllDepartments,
  getAllEmployees,
} from "../../../../api/onboarddeboard";

const ShiftChange = () => {
  const token = localStorage.getItem("token");

  const [filterType, setFilterType] = useState("all"); // 'all' | 'department'
  const [departmentId, setDepartmentId] = useState("");
  const [currentShiftId, setCurrentShiftId] = useState("");
  const [newShiftId, setNewShiftId] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [errors, setErrors] = useState({});

  const [shifts, setShifts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allotments, setAllotments] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [shiftRes, deptRes, employeeRes, allotRes] = await Promise.all([
        getAllShifts(token),
        getAllDepartments(token),
        getAllEmployees(token),
        getAllShiftAllotments(token),
      ]);

      setShifts((shiftRes.data || []).filter((s) => s.isActive));
     setDepartments(deptRes.data || []);
      setAllUsers((employeeRes.data || []).filter((u) => u.deboardEmp !== true));
      setAllotments(allotRes.data || []);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchData();
  }, [token, fetchData]);

  const allotmentMap = React.useMemo(() => {
    const map = {};
   allotments.forEach((a) => {
  if (a.user?.id) map[a.user.id] = a.shift;
});
    return map;
  }, [allotments]);

  const filteredUsers = React.useMemo(() => {
    let users = allUsers;

    if (filterType === "department" && departmentId) {
      users = users.filter(
        (u) => String(u.department?.id ?? u.departmentId) === String(departmentId)
      );
    }

    if (currentShiftId) {
      users = users.filter(
        (u) => String(allotmentMap[u.userInfoId]?.id) === String(currentShiftId)
      );
    }

    return users;
  }, [allUsers, filterType, departmentId, currentShiftId, allotmentMap]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  };

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => (prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]));
    setErrors((prev) => ({ ...prev, selectedUserIds: "" }));
  };

  const toggleAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((u) => u.userInfoId));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!currentShiftId) newErrors.currentShiftId = "Current Shift is required";
    if (selectedUserIds.length === 0) toast.error("Please select at least one employee");
    if (!newShiftId) newErrors.newShiftId = "New Shift is required";
    if (currentShiftId && newShiftId && currentShiftId === newShiftId) newErrors.newShiftId = "New Shift must be different from Current Shift";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    let successCount = 0;
    let failCount = 0;

    try {
      await Promise.all(
        selectedUserIds.map((uid) =>
          assignShiftAllotment(uid, newShiftId, token)
            .then(() => successCount++)
            .catch(() => failCount++)
        )
      );

      if (successCount > 0) toast.success(`Shift changed successfully for ${successCount} employee${successCount > 1 ? "s" : ""}`);
      if (failCount > 0) toast.error(`Failed to change shift for ${failCount} employee${failCount > 1 ? "s" : ""}`);

      handleCancel();
      fetchData();
    } catch (err) {
      toast.error("Error changing shift");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFilterType("all");
    setDepartmentId("");
    setCurrentShiftId("");
    setNewShiftId("");
    setSelectedUserIds([]);
    setErrors({});
    setCurrentPage(1);
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan="5" className="text-center py-4">
            <output className="d-flex justify-content-center align-items-center" aria-live="polite">
              <div className="spinner-border text-primary" aria-hidden="true" />
              <span style={{ marginLeft: "10px" }}>Loading...</span>
            </output>
          </td>
        </tr>
      );
    }

    if (getPaginatedData().length > 0) {
      return getPaginatedData().map((user, index) => (
        <tr
          key={`user-row-${user.userInfoId}`}
          onClick={() => toggleUser(user.userInfoId)}
          style={{ cursor: "pointer", backgroundColor: selectedUserIds.includes(user.userInfoId) ? "#eef2fb" : undefined }}
        >
          <td style={{ textAlign: "center" }}>
            <Form.Check
              type="checkbox"
              checked={selectedUserIds.includes(user.userInfoId)}
              onChange={() => toggleUser(user.userInfoId)}
              onClick={(e) => e.stopPropagation()}
            />
          </td>
          <td style={{ textAlign: "center" }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
          <td>{user.name || "-"}</td>
          <td>{user.email || "-"}</td>
          <td style={{ textAlign: "center" }}>
            {allotmentMap[user.userInfoId]?.shiftName ? (
              <span style={{ display: "inline-block", padding: "2px 10px", border: "1px solid #2E5CB8", borderRadius: "4px", color: "#2E5CB8", fontSize: "12px" }}>
                {allotmentMap[user.userInfoId].shiftName}
              </span>
            ) : (
              <span className="text-muted">-</span>
            )}
          </td>
        </tr>
      ));
    }

    return (
      <tr>
        <td colSpan="5" className="text-center">
          {currentShiftId ? "No employees found on the selected shift" : "Select a current shift to list employees"}
        </td>
      </tr>
    );
  };

  return (
    <Container fluid className="p-4">
      <style>{`
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
      `}</style>
      <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Shift Change</h4>
      <ToastContainer position="top-right" autoClose={3000} />
      <Breadcrumb className="lc-breadcrumb">
        <Breadcrumb.Item href="#">Leave</Breadcrumb.Item>
        <Breadcrumb.Item href="#">Shift Management</Breadcrumb.Item>
        <Breadcrumb.Item active>Shift Change</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-4">
        <Col xl={12}>
          <Card>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Form.Label className="col-sm-2 col-form-label form-label">Selection Type</Form.Label>
                  <Col sm={10} className="d-flex align-items-center gap-4">
                    <Form.Check
                      type="radio"
                      id="filter-all"
                      label="All Employees"
                      name="filterType"
                      value="all"
                      checked={filterType === "all"}
                      onChange={() => { setFilterType("all"); setDepartmentId(""); setSelectedUserIds([]); setCurrentPage(1); }}
                    />
                    <Form.Check
                      type="radio"
                      id="filter-dept"
                      label="Department Wise"
                      name="filterType"
                      value="department"
                      checked={filterType === "department"}
                      onChange={() => { setFilterType("department"); setSelectedUserIds([]); setCurrentPage(1); }}
                    />
                  </Col>
                </Row>

                <Row>
                  {filterType === "department" && (
                    <Col md={6}>
                      <Row className="mb-3">
                        <Form.Label className="col-sm-4 col-form-label form-label">Department <span className="text-danger">*</span></Form.Label>
                        <Col sm={8}>
                          <Form.Select value={departmentId} onChange={(e) => { setDepartmentId(e.target.value); setSelectedUserIds([]); setCurrentPage(1); }}>
                            <option value="">Select Department</option>
                            {departments.map((d, index) => (<option key={`dept-${d.id}-${index}`} value={d.id}>{d.departmentName}</option>))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Col>
                  )}

                  <Col md={6}>
                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">Current Shift <span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select
                          value={currentShiftId}
                          onChange={(e) => { setCurrentShiftId(e.target.value); setSelectedUserIds([]); setCurrentPage(1); setErrors((prev) => ({ ...prev, currentShiftId: "" })); }}
                        >
                          <option value="">Select Current Shift</option>
                          {shifts.map((s, index) => (<option key={`shift-${s.id}-${index}`} value={s.id}>{s.shiftName}</option>))}
                        </Form.Select>
                        {errors.currentShiftId && <div style={{ fontSize: "13px", color: "red" }}>{errors.currentShiftId}</div>}
                      </Col>
                    </Row>
                  </Col>

                  <Col md={6}>
                    <Row className="mb-3">
                      <Form.Label className="col-sm-4 col-form-label form-label">New Shift <span className="text-danger">*</span></Form.Label>
                      <Col sm={8}>
                        <Form.Select value={newShiftId} onChange={(e) => { setNewShiftId(e.target.value); setErrors((prev) => ({ ...prev, newShiftId: "" })); }}>
                          <option value="">Select New Shift</option>
                          {currentShiftId && shifts.filter((s) => String(s.id) !== String(currentShiftId)).map((s, index) => (
                            <option key={`newshift-${s.id}-${index}`} value={s.id}>{s.shiftName}</option>
                          ))}
                        </Form.Select>
                        {errors.newShiftId && <div style={{ fontSize: "13px", color: "red" }}>{errors.newShiftId}</div>}
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row className="align-items-center mt-5">
                  <Col className="mt-2 d-flex align-items-center justify-content-center">
                    <Button variant="primary" type="submit" disabled={isSubmitting} style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}>
                      {isSubmitting ? "Changing..." : "Change Shift"}
                    </Button>
                    <Button variant="secondary" type="button" style={{ marginLeft: "10px" }} onClick={handleCancel}>Cancel</Button>
                  </Col>
                </Row>

                {errors.selectedUserIds && (
                  <div style={{ fontSize: "13px", color: "red", marginBottom: "8px" }}>{errors.selectedUserIds}</div>
                )}

                <Table bordered hover responsive style={{ marginTop: "20px" }}>
                  <thead>
                    <tr>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", textAlign: "center", width: "5%" }}>
                        <Form.Check
                          type="checkbox"
                          checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
                          onChange={toggleAll}
                          disabled={filteredUsers.length === 0}
                        />
                      </th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", textAlign: "center", width: "8%" }}>Sr. No.</th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "35%" }}>Employee Name</th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", width: "30%" }}>Email</th>
                      <th style={{ backgroundColor: "#2E5CB8", color: "white", textAlign: "center", width: "22%" }}>Current Shift</th>
                    </tr>
                  </thead>
                  <tbody>{renderTableBody()}</tbody>
                </Table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-end">
                    <Pagination>
                      <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                      {[...Array(totalPages).keys()].map((number) => (
                        <Pagination.Item key={number + 1} active={number + 1 === currentPage} onClick={() => setCurrentPage(number + 1)}>{number + 1}</Pagination.Item>
                      ))}
                      <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    </Pagination>
                  </div>
                )}
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ShiftChange;
